import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { UserRole } from "../types";

const IS_MOCK_AUTH = !import.meta.env.VITE_USER_POOL_ID || import.meta.env.VITE_USER_POOL_ID === "";

const MOCK_USER = {
  userId: "mock-user-001",
  orgId: "mock-org-001",
  role: "admin" as UserRole,
  email: "admin@nimbus.dev",
  name: "Dev Admin",
  token: "mock-token",
};

interface AuthState {
  userId: string;
  orgId: string;
  role: UserRole;
  email: string;
  name: string;
  token: string;
}

interface AuthContextType {
  user: AuthState | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  userId: string;
  orgId: string;
  role: UserRole | "";
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  newPasswordRequired: boolean;
  completeNewPassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  userId: "",
  orgId: "",
  role: "",
  signIn: async () => {},
  signOut: () => {},
  newPasswordRequired: false,
  completeNewPassword: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [pendingCognitoUser, setPendingCognitoUser] = useState<any>(null);
  const [pendingUserAttributes, setPendingUserAttributes] = useState<any>(null);

  useEffect(() => {
    if (IS_MOCK_AUTH) {
      // Auto-login in dev mode when no Cognito is configured
      setUser(MOCK_USER);
      setIsLoading(false);
      return;
    }

    // Real Cognito auth
    import("amazon-cognito-identity-js").then(({ CognitoUserPool }) => {
      const userPool = new CognitoUserPool({
        UserPoolId: import.meta.env.VITE_USER_POOL_ID,
        ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      });
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.getSession((err: Error | null, session: any) => {
          if (!err && session?.isValid()) {
            const idToken = session.getIdToken();
            const payload = idToken.decodePayload();
            setUser({
              userId: payload.sub,
              orgId: payload["custom:orgId"] || "",
              role: (payload["custom:role"] || "sales_rep") as UserRole,
              email: payload.email || "",
              name: `${payload.given_name || ""} ${payload.family_name || ""}`.trim(),
              token: idToken.getJwtToken(),
            });
          }
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });
  }, []);

  const signIn = async (email: string, password: string) => {
    if (IS_MOCK_AUTH) {
      // In mock mode, any credentials work — pick role based on email
      const role: UserRole = email.includes("manager") ? "sales_manager" : email.includes("rep") ? "sales_rep" : "admin";
      setUser({ ...MOCK_USER, email, name: email.split("@")[0], role });
      return;
    }

    const { CognitoUserPool, CognitoUser, AuthenticationDetails } = await import("amazon-cognito-identity-js");
    const userPool = new CognitoUserPool({
      UserPoolId: import.meta.env.VITE_USER_POOL_ID,
      ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
    });

    return new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const idToken = session.getIdToken();
          const payload = idToken.decodePayload();
          setUser({
            userId: payload.sub,
            orgId: payload["custom:orgId"] || "",
            role: (payload["custom:role"] || "sales_rep") as UserRole,
            email: payload.email || "",
            name: `${payload.given_name || ""} ${payload.family_name || ""}`.trim(),
            token: idToken.getJwtToken(),
          });
          resolve();
        },
        onFailure: (err) => reject(err),
        newPasswordRequired: (userAttributes) => {
          setPendingCognitoUser(cognitoUser);
          setPendingUserAttributes(userAttributes);
          setNewPasswordRequired(true);
          resolve();
        },
      });
    });
  };

  const completeNewPassword = async (newPassword: string) => {
    if (!pendingCognitoUser) throw new Error("No pending password change");

    return new Promise<void>((resolve, reject) => {
      pendingCognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (session: any) => {
          const idToken = session.getIdToken();
          const payload = idToken.decodePayload();
          setUser({
            userId: payload.sub,
            orgId: payload["custom:orgId"] || "",
            role: (payload["custom:role"] || "sales_rep") as UserRole,
            email: payload.email || "",
            name: `${payload.given_name || ""} ${payload.family_name || ""}`.trim(),
            token: idToken.getJwtToken(),
          });
          setNewPasswordRequired(false);
          setPendingCognitoUser(null);
          setPendingUserAttributes(null);
          resolve();
        },
        onFailure: (err: Error) => reject(err),
      });
    });
  };

  const signOut = () => {
    if (IS_MOCK_AUTH) {
      setUser(null);
      return;
    }
    import("amazon-cognito-identity-js").then(({ CognitoUserPool }) => {
      const userPool = new CognitoUserPool({
        UserPoolId: import.meta.env.VITE_USER_POOL_ID,
        ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      });
      userPool.getCurrentUser()?.signOut();
      setUser(null);
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        userId: user?.userId || "",
        orgId: user?.orgId || "",
        role: user?.role || "",
        signIn,
        signOut,
        newPasswordRequired,
        completeNewPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
